import React, { useState, useEffect, useRef } from 'react';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from 'recharts';
import { Settings, LogOut, ChartNoAxesCombined, X, Cloud, CloudRain, CloudLightning, Sun } from 'lucide-react';
import './Dashboardv2Page.css';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import logo from '../../logo.svg';
import { dashboardService } from '../../services/dashboardService';
import { useAuth } from '../../context/AuthContext';
import TideChart from '../../components/TideChart';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});


const LeafletMap = ({ markers = [] }) => {
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const tileLayerRef = useRef(null);
    const markersLayerRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);


    useEffect(() => {
        let isMounted = true;

        const loadLeaflet = () => {
            // Check for JS
            if (!window.L) {
                const script = document.createElement('script');
                script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
                script.async = true;
                script.onload = () => {
                    if (isMounted) initMap();
                };
                document.head.appendChild(script);
            } else {
                initMap();
            }
        };

        const initMap = () => {
            if (!isMounted) return;
            if (mapInstanceRef.current) return;
            if (!mapContainerRef.current) return;
            if (!window.L) return;

            try {

                const map = window.L.map(mapContainerRef.current).setView([2.19, 102.24], 13);
                mapInstanceRef.current = map;


                tileLayerRef.current = window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                    maxZoom: 19
                }).addTo(map);


                markersLayerRef.current = window.L.layerGroup().addTo(map);

                setIsLoaded(true);

                setTimeout(() => {
                    if (isMounted && map) map.invalidateSize();
                }, 200);

            } catch (error) {
                console.error("Map initialization failed:", error);
            }
        };

        loadLeaflet();

        return () => {
            isMounted = false;
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);


    useEffect(() => {

        if (!isLoaded || !markersLayerRef.current || !window.L) return;


        markersLayerRef.current.clearLayers();


        const createCustomIcon = (color, pulse = false) => {
            return window.L.divIcon({
                className: 'custom-div-icon',
                html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 10px ${color};" class="${pulse ? 'marker-pulse' : ''}"></div>`,
                iconSize: [12, 12],
                iconAnchor: [6, 6]
            });
        };

        // C. Loop through data and add new markers
        markers.forEach(item => {
            let position = null;
            if (item.position && item.position[0] && item.position[1]) {
                position = item.position;
            } else if (item.lat !== undefined && item.lon !== undefined) {
                position = [item.lat, item.lon];
            } else if (item.lat !== undefined && item.lng !== undefined) {
                position = [item.lat, item.lng];
            }

            if (!position || !position[0] || !position[1]) return;

            const color = item.color || '#3b82f6';
            const pulse = item.status === 'active' || item.status === 'warning';

            const marker = window.L.marker(position, {
                icon: createCustomIcon(color, pulse)
            });

            // Bind Popup with dynamic content
            marker.bindPopup(`<b>${item.name}</b><br>Status: ${item.status || 'Normal'}`);

            // Add to the LayerGroup (not directly to map)
            marker.addTo(markersLayerRef.current);
        });

    }, [markers, isLoaded]);

    return (
        <div className="relative w-full h-full">
            {!isLoaded && <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-400 text-xs">Loading Map Interface...</div>}
            <div ref={mapContainerRef} className="w-full h-full min-h-[300px] z-0" />
        </div>
    );
};

const WaterWave = () => (
    <>
        <style>
            {`
                @keyframes wave {
                    0% { margin-left: 0; }
                    100% { margin-left: -100%; }
                }
            `}
        </style>
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
            {/* Main water body */}
            <div className="absolute bottom-0 w-full h-full bg-cyan-400/70"></div>
            {/* Two overlapping animated waves for the surface effect */}
            <div
                className="absolute -bottom-1 w-[200%] h-4 bg-cyan-300/50 rounded-[45%]"
                style={{ animationName: 'wave', animationDuration: '4s', animationTimingFunction: 'linear', animationIterationCount: 'infinite' }}
            />
            <div
                className="absolute -bottom-1 w-[200%] h-4 bg-cyan-400/60 rounded-[40%]"
                style={{ animationName: 'wave', animationDuration: '6s', animationTimingFunction: 'linear', animationIterationCount: 'infinite', animationDirection: 'reverse' }}
            />
        </div>
    </>
);

const WaterLevelGraphic = ({ levels }) => {
    const MAX_LEVEL = 2.0; // The max height in meters for calculating percentages

    const WaterColumn = ({ value, label }) => {
        const heightPercent = Math.min((value / MAX_LEVEL) * 100, 100);
        return (
            <div className="relative w-full h-full flex flex-col justify-end">
                {/* Water Fill */}
                <div
                    className="relative w-full transition-all duration-500"
                    style={{ height: `${heightPercent}%` }}
                >
                    {/* Water Value Text */}
                    <div className="absolute -top-5 w-full text-center text-xs font-bold text-black whitespace-nowrap">
                        {value.toFixed(2)}m
                    </div>
                    <WaterWave />
                </div>
                {/* Bottom Label */}
                <div className="absolute -bottom-6 w-full text-center">
                    <span className="text-xs font-semibold text-black bg-white/70 px-2 py-0.5 border border-gray-500 rounded whitespace-nowrap">
                        {label}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="relative w-full h-full">
            {/* Water Columns */}
            <div className="absolute top-[12.5%] left-0 w-full h-[75%] flex items-end z-10">
                <div className="w-1/3 h-full"><WaterColumn value={levels?.sea || 0} label="SEA" /></div>
                <div className="w-1/3 h-full mr-1"><WaterColumn value={levels?.sump || 0} label="SUMP" /></div>
                <div className="w-1/3 h-full"><WaterColumn value={levels?.drain || 0} label="DRAIN" /></div>
            </div>

            {/* Dividers */}
            <div className="absolute top-0 left-[33.33%] h-[88%] w-2 bg-slate-600 rounded-t-sm z-20">
                <div className="absolute -top-5 text-xs font-semibold whitespace-nowrap">T/Gate</div>
            </div>
            <div className="absolute top-0 left-[66.66%] h-[88%] w-2 bg-slate-600 rounded-t-sm z-20">
                <div className="absolute -top-5 text-xs font-semibold whitespace-nowrap">T/Screen</div>
            </div>

            {/* Threshold Lines */}
            {/* Position calculated as: top_offset + (1 - (level / MAX_LEVEL)) * water_area_height */}
            {/* DANGER (2.0m): 12.5% + (1 - 2.0/2.5) * 75% = 27.5% */}
            <div className="absolute top-[10.5%] w-full border-t-2 border-red-500 z-0"></div>
            {/* WARNING (1.5m): 12.5% + (1 - 1.5/2.0) * 75% = 42.5% */}
            <div className="absolute top-[29.25%] w-full border-t-2 border-orange-400 z-0"></div>
            {/* NORMAL (0.5m): 12.5% + (1 - 0.5/2.0) * 75% = 72.5% */}
            <div className="absolute top-[66.75%] w-full border-t-2 border-green-500 z-0"></div>
        </div>
    );
};

const ThresholdLegend = () => (
    <div className="space-y-1 mt-4">
        <div className="flex items-center justify-end"><span className="text-xs font-bold text-black pr-2">DANGER - 2.0m</span><div className="w-4 h-1 bg-red-500"></div></div>
        <div className="flex items-center justify-end"><span className="text-xs font-bold text-black pr-2">WARNING - 1.5m</span><div className="w-4 h-1 bg-orange-400"></div></div>
        <div className="flex items-center justify-end"><span className="text-xs font-bold text-black pr-2">NORMAL - 0.5m</span><div className="w-4 h-1 bg-green-500"></div></div>
    </div>
);


const PumpGauge = ({ name, value, status, onClick }) => {
    const getRotationAngle = (status) => {
        switch (status?.toUpperCase()) {
            case 'RUN':
            case 'ON':
                return 60; // Point to green
            case 'STANDBY':
            case 'OFF':
                return 0; // Point to yellow
            case 'TRIP':
                return -60; // Point to red
            default:
                return 0;
        }
    };

    const isTrip = status?.toUpperCase() === 'TRIP';
    const isStandby = status?.toUpperCase() === 'STANDBY' || status?.toUpperCase() === 'OFF';
    const isRun = status?.toUpperCase() === 'RUN' || status?.toUpperCase() === 'ON';

    const angle = getRotationAngle(status);
    const rad = (angle * Math.PI) / 180;
    const needleLength = 20;
    const needleX = 50 + needleLength * Math.sin(rad);
    const needleY = 50 - needleLength * Math.cos(rad);

    return (
        <div className="flex flex-col items-center">
            <div className="relative w-56 h-28 mb-2">
                {/* SVG for the gauge background */}
                <svg viewBox="0 0 100 50" className="w-full h-full">
                    {/* Dial Face Background */}
                    <path d="M 8 50 A 42 42 0 0 1 92 50 Z" fill="#090f1a" />

                    {/* Dial sectors */}
                    <path
                        d="M 10 50 A 40 40 0 0 1 30 15.36 L 45 36.34 A 10 10 0 0 0 40 50 Z"
                        fill="#ff3b30"
                        opacity={isTrip ? 1.0 : 0.15}
                        style={isTrip ? { filter: 'drop-shadow(0 0 6px #ff3b30)' } : {}}
                    />
                    <path
                        d="M 30 15.36 A 40 40 0 0 1 70 15.36 L 55 36.34 A 10 10 0 0 0 45 36.34 Z"
                        fill="#ffcc00"
                        opacity={isStandby ? 1.0 : 0.15}
                        style={isStandby ? { filter: 'drop-shadow(0 0 6px #ffcc00)' } : {}}
                    />
                    <path
                        d="M 70 15.36 A 40 40 0 0 1 90 50 L 60 50 A 10 10 0 0 0 55 36.34 Z"
                        fill="#34c759"
                        opacity={isRun ? 1.0 : 0.15}
                        style={isRun ? { filter: 'drop-shadow(0 0 6px #34c759)' } : {}}
                    />

                    {/* Bezel Ring */}
                    <path d="M 8 50 A 42 42 0 0 1 92 50" fill="none" stroke="rgba(59, 130, 246, 0.45)" strokeWidth="1.5" />
                    <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="rgba(255, 255, 255, 0.15)" strokeWidth="0.5" />

                    {/* Sector Text Labels directly inside SVG */}
                    <text x="28" y="38" fill="#ffffff" opacity={isTrip ? 1.0 : 0.4} fontSize="4.5" fontWeight="bold" textAnchor="middle">TRIP</text>
                    <text x="50" y="24" fill="#ffffff" opacity={isStandby ? 1.0 : 0.4} fontSize="4.5" fontWeight="bold" textAnchor="middle">STANDBY</text>
                    <text x="72" y="38" fill="#ffffff" opacity={isRun ? 1.0 : 0.4} fontSize="4.5" fontWeight="bold" textAnchor="middle">RUN</text>

                    {/* Glowing Vector Needle */}
                    <line
                        x1="50"
                        y1="50"
                        x2={needleX}
                        y2={needleY}
                        stroke="#00f0ff"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        style={{ filter: 'drop-shadow(0 0 5px rgba(0, 240, 255, 0.85))' }}
                    />

                    {/* Bezel center cap */}
                    <circle cx="50" cy="50" r="5.5" fill="#090f1a" stroke="#00f0ff" strokeWidth="2.5" style={{ filter: 'drop-shadow(0 0 3px rgba(0, 240, 255, 0.5))' }} />
                </svg>
            </div>
            <div
                className={`bg-[#0c162d] text-white border border-blue-500/50 shadow-[0_0_10px_rgba(59,130,246,0.3)] rounded-md px-8 py-1.5 font-bold text-sm tracking-wide uppercase transition-all duration-300 ${onClick ? 'cursor-pointer hover:border-blue-400 hover:text-blue-300' : ''
                    }`}
                onClick={onClick}
            >
                {name}
            </div>
        </div>
    );
};

const WaterLevelChartModal = ({ isOpen, onClose, locationId }) => {
    const [wl1Data, setWl1Data] = useState([]);
    const [wl2Data, setWl2Data] = useState([]);
    const [wl3Data, setWl3Data] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const fetchChartData = async () => {
                setLoading(true);
                try {
                    const [data1, data2, data3] = await Promise.all([
                        dashboardService.getWaterLevelHistory(`${locationId}_WL1`),
                        dashboardService.getWaterLevelHistory(`${locationId}_WL2`),
                        dashboardService.getWaterLevelHistory(`${locationId}_WL3`)
                    ]);
                    setWl1Data(data1);
                    setWl2Data(data2);
                    setWl3Data(data3);
                } catch (error) {
                    console.error("Failed to fetch water level history:", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchChartData();
        }
    }, [isOpen, locationId]);

    if (!isOpen) return null;

    const Chart = ({ data, title, color }) => (
        <div className="p-4 border rounded-lg shadow-sm bg-gray-50">
            <h4 className="font-semibold text-gray-700 mb-2">{title}</h4>
            <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data} margin={{ top: 5, right: 20, left: 15, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} label={{ value: 'Level (m)', angle: -90, position: 'insideLeft', offset: 10, fontSize: 12 }} />
                    <Tooltip position={{ y: 0 }} animationDuration={0} formatter={(value) => `${value.toFixed(2)} m`} />
                    <Line isAnimationActive={false} type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 transition-opacity duration-300"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl transform transition-all duration-300 scale-95 opacity-0 animate-fade-in-scale"
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
            >
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Water Level History (Last 6 Hours)</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200">
                        <X size={24} />
                    </button>
                </div>
                {loading ? (
                    <div className="text-center py-20">Loading charts...</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Chart data={wl1Data} title="Water Level 1 (SEA)" color="#3b82f6" />
                        <Chart data={wl2Data} title="Water Level 2 (SUMP)" color="#8b5cf6" />
                        <Chart data={wl3Data} title="Water Level 3 (DRAIN)" color="#10b981" />
                    </div>
                )}
            </div>
        </div>
    );
};

const PumpHistoryChartModal = ({ isOpen, onClose, pumpName, locationId }) => {
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && pumpName) {
            const fetchHistory = async () => {
                setLoading(true);
                try {
                    const hours = 12;
                    const data = pumpName === 'PUMP 1'
                        ? await dashboardService.getPump1History(locationId, hours)
                        : await dashboardService.getPump2History(locationId, hours);

                    // Extract the array (pump1Activity or pump2Activity)
                    const activityKey = Object.keys(data).find(k => k.includes('Activity'));
                    const list = activityKey ? data[activityKey] : [];

                    // Reverse to show oldest to newest left-to-right
                    setHistoryData([...list].reverse());
                } catch (error) {
                    console.error("Failed to fetch pump history:", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchHistory();
        }
    }, [isOpen, pumpName, locationId]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 transition-opacity duration-300" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl transform transition-all duration-300 scale-95 opacity-0 animate-fade-in-scale" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">{pumpName} History (Last 12 Hours)</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={24} /></button>
                </div>
                {loading ? <div className="text-center py-20">Loading history...</div> : (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={historyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" />
                            <YAxis domain={[0, 1]} ticks={[0, 1]} tickFormatter={v => v === 1 ? 'ON' : 'OFF'} />
                            <Tooltip formatter={v => v === 1 ? 'ON' : 'OFF'} />
                            <Line type="stepAfter" dataKey="value" stroke="#2563eb" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

const WeatherForecastWidget = ({ forecastData = [], radarUrl = null }) => {
    const [activeTab, setActiveTab] = useState('radar'); // 'forecast' | 'radar'

    const getIconForForecast = (forecast) => {
        const forecastLower = forecast?.toLowerCase() || '';
        if (forecastLower.includes('ribut petir')) return <CloudLightning className="w-10 h-10 text-yellow-400" />;
        if (forecastLower.includes('hujan')) return <CloudRain className="w-10 h-10 text-blue-400" />;
        if (forecastLower.includes('tiada hujan') || forecastLower.includes('cerah')) return <Sun className="w-10 h-10 text-orange-400" />;
        return <Cloud className="w-10 h-10 text-gray-400" />;
    };

    const formatDay = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
    };

    const today = forecastData?.[0];

    if (!today?.location) {
        return (
            <div className="bg-white p-4 rounded-lg shadow-md h-[350px] flex items-center justify-center">
                <div className="text-gray-500">Loading weather data...</div>
            </div>
        );
    }

    return (
        <div className="dark-card flex flex-col min-h-[350px]">
            {/* Header with Tabs */}
            <div className="flex justify-between items-center mb-4 border-b border-blue-500/20 pb-2">
                <h4 className="font-bold text-white tracking-wide uppercase text-sm truncate max-w-[50%]">
                    {activeTab === 'forecast' ? 'MELAKA' : 'MET Radar'}
                </h4>
                <div className="flex bg-slate-950/80 border border-slate-700/50 rounded-lg p-0.5">
                    <button
                        onClick={() => setActiveTab('forecast')}
                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${activeTab === 'forecast' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                            }`}
                    >
                        Forecast
                    </button>
                    <button
                        onClick={() => setActiveTab('radar')}
                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${activeTab === 'radar' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                            }`}
                    >
                        Radar
                    </button>
                </div>
            </div>

            {/* Content */}
            {activeTab === 'forecast' ? (
                <div className="flex flex-col h-full justify-between animate-fade-in">
                    <div className="flex items-center gap-4 mb-3">
                        <div className="flex-shrink-0">{getIconForForecast(today.summary_forecast)}</div>
                        <div className="flex-grow">
                            <p className="text-2xl font-bold text-white">{today.max_temp}°C</p>
                            <p className="text-sm text-slate-400 capitalize">{today.summary_forecast}</p>
                        </div>
                        <div className="text-right text-sm text-slate-300">
                            <p className="font-bold text-white">H: {today.max_temp}°</p>
                            <p>L: {today.min_temp}°</p>
                        </div>
                    </div>
                    <div className="flex space-x-4 overflow-x-auto pb-2 scrollbar-hide" style={{ height: '140px' }}>
                        {forecastData.map((day, index) => (
                            <div key={index} className="flex flex-col items-center justify-between flex-shrink-0 w-20 h-full p-2 rounded-lg bg-slate-900/60 border border-slate-800">
                                <p className="text-sm font-bold text-slate-400">{formatDay(day.date)}</p>
                                <div className="my-1 scale-90">{getIconForForecast(day.summary_forecast)}</div>
                                <p className="text-xs text-slate-300">{day.max_temp}°/{day.min_temp}°</p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="flex-grow flex items-center justify-center bg-slate-950/50 border border-slate-800 rounded-lg overflow-hidden relative h-full">
                    {radarUrl ? (
                        <img
                            src={radarUrl}
                            alt="Radar"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="text-xs text-gray-500">Loading Radar...</div>
                    )}
                    {radarUrl && (
                        <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow animate-pulse">
                            LIVE
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// const CustomizedDot = (props) => {
//     const { cx, cy, payload } = props;

//     // Only render custom labels for specific points (high/low tides)
//     if (payload.isPeak) {
//       const isHighTide = payload.level > 1.5;
//       const yOffset = isHighTide ? -45 : 15;
//       const arrow = isHighTide ? '↑' : '↓';

//       return (
//         <>
//           <circle cx={cx} cy={cy} r={4} stroke="#3b82f6" strokeWidth={2} fill="#fff" />
//           <foreignObject x={cx - 50} y={cy + yOffset} width={100} height={40}>
//             <div className="text-center bg-white/80 backdrop-blur-sm border border-gray-300 rounded-md p-1 shadow-lg">
//               <div className="text-xs font-bold text-gray-800">{payload.time}</div>
//               <div className={`text-xs font-semibold ${isHighTide ? 'text-blue-600' : 'text-green-600'}`}>
//                 {arrow} {payload.level.toFixed(1)}m
//               </div>
//             </div>
//           </foreignObject>
//         </>
//       );
//     }

//     return null; // Render nothing for normal points
// };


const LOCATIONS = [
    { id: 'PH1', name: 'RUMAH PAM PULAU MELAKA', lat: 2.186486, lon: 102.242632 },
    { id: 'PH2', name: 'RUMAH PAM PULAU KELAPA', lat: 2.193100, lon: 102.261200 }
];

const Dashboardv2Page = () => {
    const [time, setTime] = useState(new Date());
    const [selectedLocation, setSelectedLocation] = useState(LOCATIONS[0]);
    const [dashboardData, setDashboardData] = useState(null);
    const [mapLocations, setMapLocations] = useState([]);
    const [weatherForecast, setWeatherForecast] = useState([]);
    const [isChartModalOpen, setIsChartModalOpen] = useState(false);
    const [isPumpHistoryOpen, setIsPumpHistoryOpen] = useState(false);
    const [selectedPump, setSelectedPump] = useState(null);
    const [radarGifUrl, setRadarGifUrl] = useState(null);
    const [error, setError] = useState(null);
    const { logout } = useAuth();



    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const handlePumpClick = (pumpName) => {
        setSelectedPump(pumpName);
        setIsPumpHistoryOpen(true);
    };

    // This useEffect hook fetches all necessary data from the backend services.
    useEffect(() => {
        let activeRadarUrl = null;

        const processForecastData = (apiResponse) => {
            if (!apiResponse || !apiResponse.results || apiResponse.results.length === 0) {
                return [];
            }

            const results = apiResponse.results;

            const groupedByDate = results.reduce((acc, curr) => {
                const date = curr.date.split('T')[0];
                if (!acc[date]) {
                    acc[date] = {
                        date: curr.date,
                        location: { location_name: curr.locationname },
                        max_temp: null,
                        min_temp: null,
                        summary_forecast: 'N/A',
                    };
                }

                if (curr.datatype === 'FMAXT') acc[date].max_temp = curr.value;
                if (curr.datatype === 'FMINT') acc[date].min_temp = curr.value;
                if (curr.datatype === 'FSIGW') acc[date].summary_forecast = curr.value;
                if (curr.datatype === 'FGM' && (!acc[date].summary_forecast || acc[date].summary_forecast === 'N/A')) acc[date].summary_forecast = curr.value;

                return acc;
            }, {});

            return Object.values(groupedByDate);
        }

        const fetchData = async () => {
            try {
                // Fetch all data in parallel
                const [status, locations, forecast, radarBlob] = await Promise.all([
                    dashboardService.getPumpHouseV2Status(selectedLocation.id),
                    dashboardService.getMapLocations(),
                    dashboardService.getWeatherForecast(),
                    dashboardService.getMetRadarGif(),
                ]);

                // Update component state with the fetched data
                setDashboardData(status);
                setMapLocations(locations);
                setWeatherForecast(processForecastData(forecast));

                if (radarBlob instanceof Blob) {
                    if (activeRadarUrl) {
                        URL.revokeObjectURL(activeRadarUrl);
                    }
                    const newUrl = URL.createObjectURL(radarBlob);
                    activeRadarUrl = newUrl;
                    setRadarGifUrl(newUrl);
                }
            } catch (err) {
                setError('Failed to fetch dashboard data.');
                console.error(err);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 120000); // Refresh every 2 minute
        return () => {
            clearInterval(interval);
            if (activeRadarUrl) {
                URL.revokeObjectURL(activeRadarUrl);
            }
        };
    }, [selectedLocation.id]);

    // WebSocket connection for live updates
    useEffect(() => {
        let socket;
        let reconnectTimer;

        const connect = () => {
            // Replace with your actual WebSocket URL
            const wsUrl = process.env.REACT_APP_WS_URL || 'wss://nodered.netricsolutions.com/status';
            socket = new WebSocket(wsUrl);

            socket.onopen = () => {
                console.log('WebSocket Connected');
            };

            socket.onmessage = (event) => {
                try {
                    const update = JSON.parse(event.data);
                    // Filter updates by locationId if present in the WS message
                    if (update.locationId && update.locationId !== selectedLocation.id) {
                        return;
                    }
                    setDashboardData(prev => {
                        if (!prev) return update;
                        return { ...prev, ...update };
                    });
                } catch (err) {
                    console.error('WebSocket message parse error:', err);
                }
            };

            socket.onclose = () => {
                // Attempt to reconnect after 5 seconds
                reconnectTimer = setTimeout(connect, 5000);
            };

            socket.onerror = (err) => {
                console.error('WebSocket Error:', err);
                socket.close();
            };
        };

        connect();

        return () => {
            if (socket) socket.close();
            if (reconnectTimer) clearTimeout(reconnectTimer);
        };
    }, [selectedLocation.id]);

    // Handle loading and error states
    if (error) {
        return <div className="text-red-500 text-center mt-10">{error}</div>;
    }

    const fallbackData = {
        pump1: { value: 0, status: 'OFF' },
        pump2: { value: 0, status: 'OFF' },
        waterLevels: { sea: 0, sump: 0, drain: 0 }
    };
    const activeData = dashboardData || fallbackData;

    return (
        <div className="dashboard-container relative min-h-screen">
            {/* Main Content (Blurred during loading) */}
            <div className={`transition-all duration-500 ${!dashboardData ? 'filter blur-[5px] pointer-events-none select-none' : ''}`}>
                <header className="header">
                    <div className="flex items-center space-x-4">
                        <img src={logo} alt="Logo" className="h-16 bg-white/95 p-1 rounded-full shadow-[0_0_10px_rgba(255,255,255,0.4)]" />
                        <div className="flex flex-col items-start -space-y-8">
                            <h2 className="text-xl text-white font-bold">MAJLIS BANDARAYA MELAKA BERSEJARAH</h2>
                            <h1 className="text-lg text-slate-300">SPOP 2.0</h1>
                        </div>
                    </div>

                    {/* Location Selector Dropdown */}
                    <div className="flex items-center space-x-2 bg-slate-900/60 border border-slate-700/50 rounded-lg px-4 py-1.5">
                        <span className="text-slate-300 text-xs font-bold uppercase tracking-wider">RUMAH PAM:</span>
                        <select
                            value={selectedLocation.id}
                            onChange={(e) => {
                                const newLoc = LOCATIONS.find(loc => loc.id === e.target.value);
                                setSelectedLocation(newLoc);
                                setDashboardData(null); // Clear telemetry so loading triggers
                            }}
                            className="bg-slate-950/90 text-white border border-blue-500/40 rounded-md px-3 py-1.5 text-sm font-bold outline-none cursor-pointer hover:border-blue-400 transition-all"
                        >
                            {LOCATIONS.map(loc => (
                                <option key={loc.id} value={loc.id} className="bg-slate-950 text-white">{loc.id} - {loc.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center space-x-4">
                        <button onClick={() => setIsChartModalOpen(true)} className="text-white bg-rose-700 hover:bg-blue-700 p-2 rounded-full">
                            <ChartNoAxesCombined size={24} />
                        </button>
                        <button className="text-white bg-rose-700 hover:bg-blue-700 p-2 rounded-full">
                            <Settings size={24} />
                        </button>
                        <button onClick={logout} className="text-white bg-rose-700 hover:bg-blue-700 p-2 rounded-full">
                            <LogOut size={24} />
                        </button>
                    </div>
                </header>

                <WaterLevelChartModal isOpen={isChartModalOpen} onClose={() => setIsChartModalOpen(false)} locationId={selectedLocation.id} />
                <PumpHistoryChartModal isOpen={isPumpHistoryOpen} onClose={() => setIsPumpHistoryOpen(false)} pumpName={selectedPump} locationId={selectedLocation.id} />

                <main className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {/* Left Column */}
                    <div className="lg:col-span-1 md:col-span-1 col-span-1 space-y-4">
                        <div className="dark-card text-center py-6 bg-gradient-to-b from-blue-950/30 to-slate-950/50 border-blue-500/40">
                            <h3 className="text-5xl font-extrabold tracking-wider text-white drop-shadow-[0_0_10px_rgba(59,130,246,0.4)]">
                                {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </h3>
                            <p className="text-sm text-slate-400 font-semibold mt-2">
                                {time.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
                            </p>
                        </div>
                        <WeatherForecastWidget
                            forecastData={weatherForecast}
                            radarUrl={radarGifUrl}
                        />
                        <div className="dark-card min-h-[310px]">
                            <TideChart lat={selectedLocation.lat} lon={selectedLocation.lon} />
                        </div>
                    </div>

                    {/* Middle Column */}
                    <div className="lg:col-span-2 md:col-span-2 col-span-1 space-y-4">
                        <div className="dark-card">
                            <h4 className="font-bold mb-3 text-center text-xs text-white tracking-widest uppercase">MONITORING MAP LOCATION</h4>
                            <div style={{ height: '450px', width: '100%' }}><LeafletMap markers={mapLocations} /></div>
                        </div>
                        <div className="dark-card px-4 py-8">
                            {/* Metallic-blue capsule title with Valve SVGs */}
                            <div className="flex items-center justify-center gap-3 bg-gradient-to-r from-blue-950/40 via-blue-900/80 to-blue-950/40 border border-blue-500/50 rounded-full px-6 py-2 text-white font-bold mb-8 max-w-xl mx-auto shadow-[0_0_15px_rgba(59,130,246,0.15)]">
                                <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 12H3" />
                                    <path d="M12 12V5" />
                                    <circle cx="12" cy="5" r="3" />
                                    <path d="M7 9l5 3-5 3V9z" fill="currentColor" />
                                    <path d="M17 9l-5 3 5 3V9z" fill="currentColor" />
                                </svg>
                                <span className="tracking-widest text-xs sm:text-sm uppercase font-extrabold text-blue-400">{selectedLocation.id} - {selectedLocation.name}</span>
                                <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 12H3" />
                                    <path d="M12 12V5" />
                                    <circle cx="12" cy="5" r="3" />
                                    <path d="M7 9l5 3-5 3V9z" fill="currentColor" />
                                    <path d="M17 9l-5 3 5 3V9z" fill="currentColor" />
                                </svg>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <PumpGauge
                                    name="PUMP 1"
                                    value={activeData.pump1?.value || 0}
                                    status={activeData.pump1?.status || 'UNKNOWN'}
                                    onClick={() => handlePumpClick('PUMP 1')}
                                />
                                <PumpGauge
                                    name="PUMP 2"
                                    value={activeData.pump2?.value || 0}
                                    status={activeData.pump2?.status || 'UNKNOWN'}
                                    onClick={() => handlePumpClick('PUMP 2')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="lg:col-span-1 md:col-span-3 col-span-1 space-y-4">
                        <div className="dark-card p-2 relative overflow-hidden">
                            <div className="relative bg-slate-950/60 h-80 rounded-lg overflow-hidden border border-blue-500/10">
                                {/* Overlay Badge */}
                                <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-slate-950/90 border border-blue-500/40 rounded px-3 py-1 text-blue-400 text-xs font-bold uppercase tracking-widest shadow-[0_0_10px_rgba(59,130,246,0.3)]">
                                    <svg className="w-4 h-4 text-blue-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    CAMERA 1
                                </div>
                                <img
                                    src={selectedLocation.id === 'PH2' ? '/cam2img.jpeg' : '/cam1img.jpeg'}
                                    alt="Camera 1 Feed"
                                    className="w-full h-full object-cover opacity-95"
                                />
                            </div>
                        </div>

                        <div className="dark-card flex items-end justify-between pb-4 overflow-hidden" style={{
                            backgroundImage: "url('/house2.png')",
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            minHeight: '420px',
                            backgroundColor: '#e0f2fe' // Keep light blue background from target mockup
                        }}>
                            <div className="relative w-1/2 h-[220px] -ml-2">
                                <WaterLevelGraphic levels={activeData.waterLevels} />
                            </div>
                            <div className="w-1/2 flex justify-end pr-2">
                                <ThresholdLegend />
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Premium Loading Glassmorphic Overlay (Fades out when data arrives) */}
            {!dashboardData && (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/10 backdrop-blur-[4px] transition-all duration-500 animate-fade-in">
                    <div className="flex flex-col items-center p-8 bg-slate-950/75 backdrop-blur-md rounded-2xl border border-slate-800/50 shadow-[0_20px_50px_rgba(0,0,0,0.3)]">
                        {/* Modern Spinner */}
                        <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-600 rounded-full animate-spin"></div>
                        <span className="mt-4 text-sm font-extrabold tracking-wider text-slate-300 uppercase animate-pulse">
                            Fetching Live Telemetry...
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboardv2Page;
